/**
 * Preditiva Gult Premium V2
 * Copyright (c) 2026 Preditiva. Todos os direitos reservados.
 * Tema proprietario - uso exclusivo clientes Preditiva.
 */
/**
 * product-info.js — Custom Element <product-info>
 * Coordena a interação na página de produto:
 * - Troca de variantes (atualiza preço, disponibilidade, imagem)
 * - Comunicação com product-form via pub/sub
 * - Section Rendering API para atualizações parciais
 *
 *  product-info encapsula toda a lógica da PDP.
 *
 * Os dados das variantes vêm do <script type="application/json"
 * id="ProductData-{section.id}"> renderizado por sections/main-product.liquid.
 */

class ProductInfo extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.url;
    this.variantSelectors = this.querySelectorAll('[data-option-index]');
    this.currentVariant = null;
    this.productData = null;
    this.lastKnownPrice = null;
    this.renderController = null;
  }

  connectedCallback() {
    this.productData = this.getProductData();
    this.setupVariantListeners();
    this.setupGalleryThumbs();

    // Estado inicial: resolve a variante já marcada no HTML e marca
    // os valores de opção que não têm nenhuma variante em estoque.
    if (this.productData) {
      this.updateVariantFromOptions();
      if (this.currentVariant) {
        this.lastKnownPrice = this.currentVariant.price;
        // Reconcilia o botão com o JSON (fonte confiável) já no carregamento —
        // cobre o caso de a página abrir direto numa URL ?variant=<esgotado>.
        this.updateBuyState(this.currentVariant);
        this.updateQuantityLimit(this.currentVariant);
      }
      this.updateOptionAvailability();
    }
  }

  /* ========================================================================
     Variantes — escuta cliques e atualiza preço/imagem/disponibilidade
     ======================================================================== */

  setupVariantListeners() {
    this.variantSelectors.forEach((selector) => {
      const inputs = selector.querySelectorAll('input[type="radio"]');
      inputs.forEach((input) => {
        input.addEventListener('change', this.onVariantChange.bind(this));
      });
    });
  }

  onVariantChange() {
    this.updateSelectedLabel();
    this.updateVariantFromOptions();
    this.updateOptionAvailability();

    // Combinação sem variante correspondente (ex.: cor + tamanho inexistente).
    // Precisa bloquear a compra — senão o carrinho recebe a variante anterior.
    if (!this.currentVariant) {
      this.setUnavailableState();
      this.dispatchVariantChange(this.buildUnavailableVariant());
      return;
    }

    this.lastKnownPrice = this.currentVariant.price;
    this.updateURL();
    this.updateFormId();
    // Reflete a disponibilidade NA HORA. renderProductInfo() confirma depois com
    // o HTML do servidor, mas até a resposta chegar o botão não pode ficar
    // clicável numa variante esgotada.
    this.updateBuyState(this.currentVariant);
    this.updateQuantityLimit(this.currentVariant);
    this.renderProductInfo();
    this.dispatchVariantChange(this.currentVariant);
  }

  /** Habilita/desabilita o botão de compra conforme a variante escolhida. */
  updateBuyState(variant) {
    const submit = this.querySelector('.product-form__submit');
    if (!submit) return;

    if (variant.available) {
      submit.disabled = false;
      const rotulo = submit.dataset.labelAdd;
      if (rotulo) submit.textContent = rotulo;
    } else {
      submit.disabled = true;
      submit.textContent = submit.dataset.labelSoldout || 'Esgotado';
    }
  }

  /**
   * Limita a quantidade ao estoque real — só quando a Shopify controla o
   * estoque E está configurada para não vender sem saldo. Sem essa checagem,
   * produtos com estoque não controlado (inventory_quantity = 0) ficariam
   * impossíveis de comprar.
   */
  updateQuantityLimit(variant) {
    const input = this.querySelector('.quantity__input');
    if (!input) return;

    const controla = variant.inventory_management &&
      variant.inventory_policy === 'deny' &&
      typeof variant.inventory_quantity === 'number' &&
      variant.inventory_quantity > 0;

    if (controla) {
      input.max = variant.inventory_quantity;
      if (parseInt(input.value, 10) > variant.inventory_quantity) {
        input.value = variant.inventory_quantity;
      }
    } else {
      input.removeAttribute('max');
    }
  }

  dispatchVariantChange(variant) {
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: {
        sectionId: this.sectionId,
        variant: variant,
      }
    }));
  }

  /**
   * Objeto sintético para as combinações inexistentes: mantém o último preço
   * conhecido (para o parcelamento não zerar) e sinaliza indisponibilidade
   * aos demais componentes (sticky ATC, comprar agora, estoque).
   */
  buildUnavailableVariant() {
    return {
      id: null,
      title: null,
      options: this.getSelectedOptions(),
      price: this.lastKnownPrice != null ? this.lastKnownPrice : 0,
      compare_at_price: null,
      available: false,
      inventory_quantity: 0,
    };
  }

  /**
   * Atualiza o rótulo "Tamanho: 38" de CADA opção com o valor selecionado.
   */
  updateSelectedLabel() {
    this.variantSelectors.forEach((selector) => {
      const checked = selector.querySelector('input[type="radio"]:checked');
      if (!checked) return;

      // Realça o item marcado dentro deste seletor
      selector.querySelectorAll('.variant-picker__item').forEach((item) => {
        item.classList.remove('variant-picker__item--selected');
      });
      checked.closest('.variant-picker__item')?.classList.add('variant-picker__item--selected');

      // Atualiza o texto do <legend> da opção correspondente
      const fieldset = selector.closest('.product-option');
      const labelEl = fieldset?.querySelector('[data-option-name]');
      if (labelEl) labelEl.textContent = checked.value;
    });
  }

  /**
   * Lê os valores marcados respeitando o índice declarado em data-option-index,
   * para não depender da ordem dos nós no DOM.
   */
  getSelectedOptions() {
    const selectedOptions = [];
    this.variantSelectors.forEach((selector) => {
      const checked = selector.querySelector('input[type="radio"]:checked');
      if (!checked) return;

      const parsed = parseInt(selector.dataset.optionIndex, 10);
      const index = Number.isNaN(parsed) ? selectedOptions.length : parsed;
      selectedOptions[index] = checked.value.trim();
    });
    return selectedOptions;
  }

  updateVariantFromOptions() {
    if (!this.productData || !Array.isArray(this.productData.variants)) {
      this.currentVariant = null;
      return;
    }

    const selectedOptions = this.getSelectedOptions();
    if (!selectedOptions.length) {
      this.currentVariant = null;
      return;
    }

    const match = this.productData.variants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;
      if (variant.options.length !== selectedOptions.length) return false;
      return variant.options.every(
        (option, index) => String(option).trim() === selectedOptions[index]
      );
    });

    this.currentVariant = match || null;
  }

  /**
   * Marca visualmente os valores que não têm variante em estoque considerando
   * as outras opções já escolhidas (ex.: cor Preta sem o tamanho 38).
   */
  updateOptionAvailability() {
    if (!this.productData || !Array.isArray(this.productData.variants)) return;

    const selectedOptions = this.getSelectedOptions();

    this.variantSelectors.forEach((selector) => {
      const parsed = parseInt(selector.dataset.optionIndex, 10);
      if (Number.isNaN(parsed)) return;

      selector.querySelectorAll('.variant-picker__item').forEach((item) => {
        const input = item.querySelector('input[type="radio"]');
        if (!input) return;

        const value = input.value.trim();
        const isAvailable = this.productData.variants.some((variant) => {
          if (!variant.available || !Array.isArray(variant.options)) return false;
          if (String(variant.options[parsed]).trim() !== value) return false;

          // As demais opções escolhidas precisam bater
          return selectedOptions.every((selected, index) => {
            if (index === parsed || selected == null) return true;
            return String(variant.options[index]).trim() === selected;
          });
        });

        item.classList.toggle('variant-picker__item--unavailable', !isAvailable);
      });
    });
  }

  /**
   * Bloqueia a compra quando a combinação escolhida não existe.
   */
  setUnavailableState() {
    const idInput = this.querySelector('input[name="id"]');
    if (idInput) idInput.dataset.available = 'false';

    const submit = this.querySelector('.product-form__submit');
    if (submit) {
      submit.disabled = true;
      submit.textContent = submit.dataset.labelUnavailable || 'Indisponível';
    }
  }

  getProductData() {
    const scriptEl = document.querySelector(`#ProductData-${this.sectionId}`);
    if (scriptEl) {
      try {
        return JSON.parse(scriptEl.textContent);
      } catch (e) {
        console.error('[product-info] Erro ao parsear product data:', e);
        return null;
      }
    }
    console.warn(`[product-info] #ProductData-${this.sectionId} não encontrado — seleção de variante desativada.`);
    return null;
  }

  /* ========================================================================
     Atualizar URL sem reload (para SEO e compartilhamento)
     ======================================================================== */

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }

  /* ========================================================================
     Atualizar ID da variante no form
     ======================================================================== */

  updateFormId() {
    if (!this.currentVariant) return;

    const formInputs = this.querySelectorAll('input[name="id"]');
    formInputs.forEach((input) => {
      input.value = this.currentVariant.id;
      input.dataset.available = this.currentVariant.available ? 'true' : 'false';
    });
  }

  /* ========================================================================
     Section Rendering API — re-renderiza preço/estoque sem reload
     ======================================================================== */

  renderProductInfo() {
    if (!this.currentVariant) return;

    const sectionId = this.sectionId;
    const url = `${this.productUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}`;

    // Cancela a requisição anterior: cliques rápidos não podem deixar
    // uma resposta antiga sobrescrever a variante mais recente.
    if (this.renderController) this.renderController.abort();
    this.renderController = new AbortController();

    fetch(url, { signal: this.renderController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Atualiza o bloco de preço (PIX, parcelamento, % de desconto — precisa
        // do Liquid do servidor mesmo).
        this.updateBlock(html, `#price-${sectionId}`);

        // O botão de comprar NÃO é atualizado aqui de propósito: ele só tem dois
        // estados possíveis ("Adicionar ao Carrinho" / "Esgotado") e updateBuyState()
        // já os aplicou de forma síncrona e confiável a partir do JSON injetado.
        // Sobrescrever com a resposta desta requisição já causou o bug de o botão
        // "voltar a ficar clicável" num tamanho esgotado, por chegar depois e
        // divergir do estado que já tínhamos calculado corretamente.
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('[product-info] Erro ao renderizar:', error);
      });
  }

  updateBlock(html, selector) {
    const oldEl = this.querySelector(selector);
    const newEl = html.querySelector(selector);
    if (oldEl && newEl) {
      oldEl.innerHTML = newEl.innerHTML;
    }
  }

  /* ========================================================================
     Galeria — Thumbnails
     ======================================================================== */

  setupGalleryThumbs() {
    const thumbs = this.querySelectorAll('.product-gallery__thumb');
    const mainImage = this.querySelector('#MainProductImage');

    if (!thumbs.length || !mainImage) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        // Atualiza classe ativa
        thumbs.forEach((t) => {
          t.classList.remove('product-gallery__thumb--active');
          t.setAttribute('aria-current', 'false');
        });
        thumb.classList.add('product-gallery__thumb--active');
        thumb.setAttribute('aria-current', 'true');

        // Atualiza imagem principal usando o src da thumbnail
        const thumbImg = thumb.querySelector('img');
        if (thumbImg) {
          // Extrai a URL base e gera uma versão de alta resolução
          const baseSrc = thumbImg.src.replace(/width=\d+/, 'width=1200');
          mainImage.src = baseSrc;
          mainImage.srcset = '';
        }
      });
    });
  }
}

customElements.define('product-info', ProductInfo);
